import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import Checklist from "@/components/sections/Checklist";
import CTABlock from "@/components/sections/CTABlock";
import AccordionGroup from "@/components/sections/AccordionGroup";
import SearchFilterThin from "@/components/sections/SearchFilterThin";
import PropertyGrid from "@/components/sections/PropertyGrid";
import heroImage from "@/assets/listings-hero.jpg";
import sample1 from "@/assets/listing-sample-1.jpg";
import sample2 from "@/assets/listing-sample-2.jpg";
import sample3 from "@/assets/listing-sample-3.jpg";
import sample4 from "@/assets/listing-sample-4.jpg";
import sample5 from "@/assets/listing-sample-5.jpg";
import sample6 from "@/assets/listing-sample-6.jpg";

const SHOW_LIVE_LISTINGS = false;

const sampleListings = [
  { image: sample1, price: "$625,000", address: "42 Maple Street, Lynn, MA", beds: 4, baths: 2, sqft: "2,100", status: "Active", href: "/listing/42-maple-street-lynn-ma" },
  { image: sample2, price: "$489,000", address: "118 Congress Ave, Boston, MA", beds: 2, baths: 2, sqft: "1,250", status: "Active" },
  { image: sample3, price: "$545,000", address: "7 Harbor Lane, Quincy, MA", beds: 3, baths: 2, sqft: "1,800", status: "Active" },
  { image: sample4, price: "$875,000", address: "23 Brattle Street, Cambridge, MA", beds: 5, baths: 3, sqft: "3,200", status: "Active" },
  { image: sample5, price: "$729,000", address: "56 Highland Ave, Somerville, MA", beds: 6, baths: 3, sqft: "2,800", status: "Active" },
  { image: sample6, price: "$1,250,000", address: "1 Waterfront Drive, Quincy, MA", beds: 4, baths: 4, sqft: "3,600", status: "Active" },
];

const areas = [
  { name: "Boston", href: "/listings?area=boston" },
  { name: "Cambridge", href: "/listings?area=cambridge" },
  { name: "Lynn", href: "/listings?area=lynn" },
  { name: "Somerville", href: "/listings?area=somerville" },
  { name: "Quincy", href: "/listings?area=quincy" },
];

const ListingsLiveExperience = () => (
  <>
    <PageHero
      compact
      title="Find Homes for Sale in Massachusetts"
      subtitle="Browse real-time listings and find the right home faster"
      backgroundImage={heroImage}
    >
      <SearchFilterThin />
    </PageHero>

    <ContentSection>
      <div className="flex items-center justify-between mb-10">
        <p className="text-muted-foreground text-sm font-body uppercase tracking-widest">{sampleListings.length} Properties</p>
        <div className="flex items-center gap-4">
          <select className="appearance-none bg-transparent font-body text-sm text-muted-foreground border border-border px-4 py-2 focus:outline-none focus:border-accent cursor-pointer" aria-label="Sort listings">
            <option>Newest First</option>
            <option>Price: Low to High</option>
            <option>Price: High to Low</option>
            <option>Most Beds</option>
          </select>
        </div>
      </div>
      <PropertyGrid listings={sampleListings} columns={3} />
      <div className="flex justify-center mt-16 gap-2">
        <span className="inline-flex items-center justify-center w-10 h-10 bg-accent text-accent-foreground font-body text-sm font-semibold">1</span>
        <span className="inline-flex items-center justify-center w-10 h-10 border border-border text-muted-foreground font-body text-sm hover:border-accent hover:text-accent transition-colors cursor-pointer">2</span>
        <span className="inline-flex items-center justify-center w-10 h-10 border border-border text-muted-foreground font-body text-sm hover:border-accent hover:text-accent transition-colors cursor-pointer">3</span>
        <span className="inline-flex items-center justify-center w-10 h-10 border border-border text-muted-foreground font-body text-sm hover:border-accent hover:text-accent transition-colors cursor-pointer">→</span>
      </div>
    </ContentSection>

    <section className="bg-secondary on-dark py-12">
      <div className="mx-auto max-w-wide px-5 md:px-12 text-center">
        <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-3">Real-Time MLS Listings</h2>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto">Listings are updated directly from MLS PIN, so you're seeing accurate and up-to-date properties</p>
      </div>
    </section>

    <ContentSection>
      <SectionHeader title="Looking for Something Specific?" />
      <div className="max-w-3xl mx-auto text-center space-y-6">
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed">Not every opportunity is easy to find. We help you identify the right properties, including off-market opportunities, and move quickly when the right home becomes available.</p>
        <Link
          to="/consultation"
          className="inline-block font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors"
        >
          Book a Consultation →
        </Link>
      </div>
    </ContentSection>

    <ContentSection variant="dark">
      <SectionHeader title="Why Work With Hadassah Homes" />
      <div className="max-w-3xl mx-auto">
        <Checklist
          items={[
            "Guidance beyond just browsing listings",
            "Help evaluating property value",
            "Support through the full buying process",
            "Local market knowledge",
          ]}
        />
      </div>
    </ContentSection>

    <ContentSection>
      <SectionHeader title="Explore Homes by Area" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
        {areas.map((area) => (
          <Link
            key={area.name}
            to={area.href}
            className="group block border border-border p-6 md:p-8 text-center transition-colors hover:border-accent/30"
          >
            <span className="font-headline text-lg md:text-xl text-foreground group-hover:text-accent transition-colors">{area.name}</span>
          </Link>
        ))}
      </div>
    </ContentSection>

    <ContentSection variant="dark">
      <SectionHeader title="Frequently Asked Questions" />
      <div className="max-w-3xl mx-auto">
        <AccordionGroup
          items={[
            { title: "Are listings updated in real time?", content: "Yes, listings are pulled directly from MLS PIN and updated regularly." },
            { title: "Can you help me find off-market properties?", content: "Yes, we can help identify opportunities beyond what's publicly listed." },
            { title: "Do I need an agent to schedule a showing?", content: "We can help you schedule showings and guide you through the process." },
          ]}
        />
      </div>
    </ContentSection>

    <CTABlock
      variant="dark"
      title="Need Help Finding the Right Home?"
      description="We're here to guide you through the process with expert advice and local market knowledge."
      primaryCta={{ text: "Book a Consultation →", href: "/consultation" }}
    />
  </>
);

const ListingsComingSoonExperience = () => (
  <>
    <PageHero
      compact
      title="Live Massachusetts Listings Coming Soon"
      subtitle="We are preparing an MLS-powered listings experience for accurate, up-to-date Massachusetts property search."
      backgroundImage={heroImage}
    >
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link
          to="/consultation"
          className="inline-flex items-center justify-center bg-accent px-8 py-4 font-body text-sm uppercase tracking-[0.12em] text-accent-foreground transition-colors hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
        >
          Book a Consultation
        </Link>
        <Link
          to="/consultation"
          className="inline-flex items-center justify-center border border-white/35 px-8 py-4 font-body text-sm uppercase tracking-[0.12em] text-white transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
        >
          Tell Us What You're Looking For
        </Link>
      </div>
    </PageHero>

    <ContentSection className="py-18 md:py-22">
      <div className="mx-auto max-w-4xl text-center space-y-8">
        <SectionHeader
          align="center"
          title="In the Meantime, We Can Help You Search Smarter"
          description="While the live listings experience is being finalized, Hadassah Homes can help you identify homes, investment properties, and off-market opportunities that match your goals."
        />
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              step: "01",
              title: "Share Your Search Criteria",
              description:
                "Tell us your location, budget, property type, and timeline.",
              cta: "Get Started",
            },
            {
              step: "02",
              title: "Book a Buyer Consultation",
              description:
                "Get guidance before touring homes or making offers.",
              cta: "Book Now",
            },
            {
              step: "03",
              title: "Ask About Opportunities",
              description:
                "Explore available and off-market options with direct support.",
              cta: "Contact Us",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="flex h-full flex-col border border-border/70 bg-card px-6 py-7 md:px-7 md:py-8 shadow-[0_18px_40px_-28px_rgba(15,23,42,0.18)]"
            >
              <p className="font-body text-[11px] font-medium uppercase tracking-[0.22em] text-accent">
                {item.step}
              </p>
              <h2 className="mt-4 font-headline text-2xl text-foreground">
                {item.title}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {item.description}
              </p>
              <Link
                to="/consultation"
                className="mt-6 inline-block font-body text-xs uppercase tracking-[0.14em] text-accent transition-colors hover:text-accent/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {item.cta} →
              </Link>
            </div>
          ))}
        </div>
        <p className="font-body text-sm uppercase tracking-[0.16em] text-accent">
          MLS-powered search is coming soon. Until then, Hadassah Homes can provide personalized property guidance directly.
        </p>
      </div>
    </ContentSection>
  </>
);

const Listings = () => {
  useEffect(() => {
    document.title = "Homes for Sale in Massachusetts | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Explore Massachusetts property opportunities with Hadassah Homes. Our live MLS-powered listings experience is coming soon, and our team can already help you search strategically.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://hadassahhomesinc.com/listings";
  }, []);

  return (
  <SiteLayout>
    {SHOW_LIVE_LISTINGS ? <ListingsLiveExperience /> : <ListingsComingSoonExperience />}
  </SiteLayout>
  );
};

export default Listings;
